import { useCallback, useEffect, useRef, useState } from 'react';
import { interviewRuntimeAPI } from '../api/interviewRuntimeAPI';
import { toPcm16 } from '../avatar/pcm';
import { SpeechStream, int16ToFloat } from './speechStream';

/**
 * Plays the interviewer's voice and exposes a live amplitude reading so the
 * avatar's mouth can move with the actual audio rather than a canned
 * animation loop — the same AudioContext/decodeAudioData/BufferSource
 * pattern already proven in WebsiteInterviewPage.js:840-880, routed through
 * an AnalyserNode instead of straight to destination so the amplitude is
 * readable without a second copy of the audio.
 *
 * getAmplitude() is a plain function, not state, on purpose: reading it
 * belongs in a requestAnimationFrame loop (InterviewAvatarStage owns that
 * loop), and putting a per-frame number into React state would re-render
 * the whole sitting screen at 60fps for a value only the avatar needs.
 */
export function useSpeechPlayback(avatar = null, voice = null) {
  const [isSpeaking, setIsSpeaking] = useState(false);
  // An avatar on trial (avatar/useAvatar.js) speaks the line instead: the
  // decoded audio is re-encoded for it, and its own stream carries the voice
  // in step with the lips, so nothing plays here. Read through a ref so the
  // callbacks below stay stable as its status changes.
  const avatarRef = useRef(avatar);
  avatarRef.current = avatar;
  const speakingViaRef = useRef(null);
  // A line playing here rather than through the face: the face, if it
  // arrives meanwhile, waits for the next line instead of appearing mid-line
  // out of step with a voice it isn't speaking.
  const [speakingPlainly, setSpeakingPlainly] = useState(false);
  const [error, setError] = useState('');
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const bufferRef = useRef(null);
  // text -> the in-flight or settled generateSpeech promise. Synthesising a
  // long opening line took over five seconds on production, all of it
  // silence after "Begin interview"; prefetch() lets the start screen spend
  // that time instead. Entries are dropped once spoken, so it never grows
  // past the line or two that are pending.
  const pendingRef = useRef(new Map());
  // text -> the line streaming in (speechStream.js): the examiner starts
  // speaking on its first tenth of a second rather than once the whole line
  // exists. The whole-line fetch above is the fallback.
  const streamsRef = useRef(new Map());
  // The line playing here from a stream, so stop() can cut it off.
  const localRef = useRef(null);

  // The picked interviewer's voice (an avatar interview), known before the
  // first line is prefetched; read through a ref so callbacks stay stable.
  const voiceRef = useRef(voice);
  voiceRef.current = voice;

  const fetchSpeech = useCallback((text) => {
    const pending = pendingRef.current;
    if (!pending.has(text)) {
      const request = interviewRuntimeAPI.generateSpeech(text, voiceRef.current);
      pending.set(text, request);
      request.catch(() => pending.delete(text));
    }
    return pending.get(text);
  }, []);

  /** Start synthesising a line before it's needed. Fetching needs no user
   * gesture - only playing does - so this is safe before unlock(). */
  const canStream = () => typeof interviewRuntimeAPI.streamSpeech === 'function';

  const getStream = useCallback((text) => {
    const streams = streamsRef.current;
    if (!streams.has(text)) {
      streams.set(text, new SpeechStream(() => interviewRuntimeAPI.streamSpeech(text, voiceRef.current)));
    }
    return streams.get(text);
  }, []);

  const prefetch = useCallback((text) => {
    if (!text || !text.trim()) return;
    if (canStream()) getStream(text);
    else fetchSpeech(text).catch(() => {});
  }, [fetchSpeech, getStream]);

  const ensureContext = useCallback(() => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioCtxRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      bufferRef.current = new Uint8Array(analyserRef.current.frequencyBinCount);
    }
    return audioCtxRef.current;
  }, []);

  /** Mobile browsers (iOS Safari in particular) only allow an AudioContext
   * to actually produce sound if resume() was called synchronously inside
   * a real user gesture's own handler - calling it later from a fetch
   * callback, as speak() otherwise would on first mount, leaves the
   * context permanently suspended and the interviewer's opening line
   * silent. Call this directly inside a tap handler (nothing awaited
   * first) before the sitting starts talking; every speak() after that
   * reuses the same already-running context. */
  const unlock = useCallback(() => {
    const ctx = ensureContext();
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => { /* still unlocked for a later attempt */ });
    }
  }, [ensureContext]);

  /** 0..1, the current playback loudness. Silent (0) when nothing is
   * playing — the avatar reads this every frame regardless of isSpeaking,
   * so it must never throw before the first speak() call. */
  const getAmplitude = useCallback(() => {
    const analyser = analyserRef.current;
    const buf = bufferRef.current;
    if (!analyser || !buf) return 0;
    analyser.getByteFrequencyData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) sum += buf[i];
    return sum / buf.length / 255;
  }, []);

  const stop = useCallback(() => {
    if (speakingViaRef.current) {
      speakingViaRef.current.interrupt();
      speakingViaRef.current = null;
    }
    if (sourceRef.current) {
      try { sourceRef.current.stop(); } catch { /* already stopped */ }
      sourceRef.current = null;
    }
    if (localRef.current) {
      localRef.current.cancel();
      localRef.current = null;
    }
    setIsSpeaking(false);
    setSpeakingPlainly(false);
  }, []);

  /** A streaming line played here, each piece scheduled straight after the
   * one before as it arrives. Rejects if the stream ends with no audio at
   * all (the caller falls back to the whole-line fetch). */
  const playHere = useCallback((stream) => new Promise((resolve, reject) => {
    const ctx = ensureContext();
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    analyserRef.current.connect(ctx.destination);
    const sources = [];
    let next = 0;
    let last = null;
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      if (localRef.current === playing) localRef.current = null;
      setIsSpeaking(false);
      setSpeakingPlainly(false);
      resolve();
    };
    const playing = {
      cancel: () => {
        sources.forEach((src) => { try { src.stop(); } catch { /* not started */ } });
        unsubscribe();
        finish();
      },
    };
    localRef.current = playing;
    setIsSpeaking(true);
    setSpeakingPlainly(true);
    // Assigned below; a stream fetched early replays its pieces inside
    // subscribe(), before it returns.
    let unsubscribe = () => {};
    unsubscribe = stream.subscribe({
      chunk: (piece) => {
        if (settled) return;
        const floats = int16ToFloat(piece);
        const buffer = ctx.createBuffer(1, floats.length, stream.sampleRate);
        buffer.getChannelData(0).set(floats);
        const src = ctx.createBufferSource();
        src.buffer = buffer;
        src.connect(analyserRef.current);
        const at = Math.max(next, ctx.currentTime + 0.03);
        src.start(at);
        next = at + buffer.duration;
        sources.push(src);
        last = src;
      },
      end: (err) => {
        if (settled) return;
        if (!last) {
          settled = true;
          if (localRef.current === playing) localRef.current = null;
          setIsSpeaking(false);
          setSpeakingPlainly(false);
          reject(err || new Error('No audio returned'));
          return;
        }
        last.onended = finish;
      },
    });
  }), [ensureContext]);

  /** A streaming line spoken by the face, piece by piece (avatar renderers'
   * open()). Rejects if the face fails or no audio comes, so the caller can
   * play it here instead. */
  const sayThroughFace = useCallback((renderer, stream) => new Promise((resolve, reject) => {
    const line = renderer.open(stream.sampleRate);
    speakingViaRef.current = renderer;
    setIsSpeaking(true);
    let any = false;
    let failed = false;
    let unsubscribe = () => {};
    unsubscribe = stream.subscribe({
      chunk: (piece) => {
        if (failed) return;
        try {
          line.push(piece);
          any = true;
        } catch (err) {
          failed = true;
          unsubscribe();
          reject(err);
        }
      },
      end: (err) => {
        if (failed) return;
        if (!any) { reject(err || new Error('No audio returned')); return; }
        line.end().then(() => {
          if (speakingViaRef.current === renderer) speakingViaRef.current = null;
          setIsSpeaking(false);
          resolve();
        });
      },
    });
  }), []);

  const speakStream = useCallback(async (stream) => {
    const renderer = avatarRef.current ? await avatarRef.current.whenReady() : null;
    stop(); // cut off anything still playing before starting the next line
    if (renderer?.open) {
      try {
        await sayThroughFace(renderer, stream);
        return;
      } catch {
        // The face failed: say the line plainly instead (the stream keeps
        // every piece, so it plays from the start).
        speakingViaRef.current = null;
      }
    }
    await playHere(stream);
  }, [playHere, sayThroughFace, stop]);

  /** The whole line fetched, decoded and played: the fallback when it
   * cannot be streamed. */
  const speakWhole = useCallback((text) => {
    const ctx = ensureContext();

    return fetchSpeech(text)
      .then((result) => new Promise((resolve, reject) => {
        pendingRef.current.delete(text);
        if (!result?.success || !result?.audio_data) {
          reject(new Error('No audio returned'));
          return;
        }
        const binary = atob(result.audio_data);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

        const resume = ctx.state === 'suspended' ? ctx.resume() : Promise.resolve();
        resume.then(() => {
          ctx.decodeAudioData(
            bytes.buffer,
            async (audioBuffer) => {
              const renderer = avatarRef.current ? await avatarRef.current.whenReady() : null;
              if (renderer) {
                try {
                  const pcm = await toPcm16(audioBuffer, renderer.sampleRate);
                  stop();
                  speakingViaRef.current = renderer;
                  setIsSpeaking(true);
                  await renderer.say(pcm, audioBuffer.duration);
                  if (speakingViaRef.current === renderer) speakingViaRef.current = null;
                  setIsSpeaking(false);
                  resolve();
                  return;
                } catch {
                  // The face failed mid-line: say it plainly instead.
                  speakingViaRef.current = null;
                }
              }
              stop(); // cut off anything still playing before starting the next line
              const source = ctx.createBufferSource();
              source.buffer = audioBuffer;
              source.connect(analyserRef.current);
              analyserRef.current.connect(ctx.destination);
              source.onended = () => {
                setIsSpeaking(false);
                setSpeakingPlainly(false);
                resolve();
              };
              sourceRef.current = source;
              setIsSpeaking(true);
              setSpeakingPlainly(true);
              source.start(0);
            },
            (decodeErr) => reject(decodeErr || new Error('Could not decode audio')),
          );
        }, reject);
      }))
      .catch((err) => {
        // Voice is enhancement, not the only way to see what was said - the
        // caption already carries the text. A synthesis failure must not
        // block the interview.
        setError(err?.message || 'Could not play audio');
        setIsSpeaking(false);
      });
  }, [ensureContext, fetchSpeech, stop]);

  /** Speak one line. Resolves once it has been said (or stop() cut it off),
   * so a caller can chain "speak, then enable the answer box" without a
   * separate onended wire-up at each call site. Streams when it can; a line
   * whose stream brings no audio is fetched whole instead. */
  const speak = useCallback((text) => {
    if (!text || !text.trim()) return Promise.resolve();
    setError('');
    if (!canStream()) return speakWhole(text);
    const stream = getStream(text);
    streamsRef.current.delete(text);
    return speakStream(stream).catch(() => speakWhole(text));
  }, [getStream, speakStream, speakWhole]);

  useEffect(() => () => {
    stop();
    if (audioCtxRef.current) {
      audioCtxRef.current.close?.();
    }
  }, [stop]);

  return { speak, prefetch, stop, unlock, isSpeaking, speakingPlainly, getAmplitude, error };
}
